import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Heart, MessageCircle, Share2 } from 'lucide-react';
import { ChartPoint, VideoPostMarker } from '../../../shared/types/dashboard';
import ChartCard from '../ui/ChartCard';
import { getTooltipTransform } from '../ui/chartUtils';
import PlayableVideoThumbnail from '../ui/PlayableVideoThumbnail';

const COLOR_LIKES = '#EC0452';
const COLOR_VIEWS = '#22C55E';
const COLOR_COMMENTS = '#94A3B8';
const COLOR_SHARES = '#F59E0B';

interface LiveEventMarker {
  id: number;
  title?: string;
  started_at: string;
  ended_at?: string;
  duration_minutes?: number;
  notes?: string;
}

interface GrowthChartProps {
  data?: ChartPoint[];
  comparisonData?: ChartPoint[];
  videoPosts?: VideoPostMarker[];
  liveEvents?: LiveEventMarker[];
  isLoading?: boolean;
  period?: string;
  username?: string;
}

const GrowthChart = ({ data, comparisonData, videoPosts = [], liveEvents = [], isLoading, period = '7d', username }: GrowthChartProps) => {
  const navigate = useNavigate();
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [hoveredCompPoint, setHoveredCompPoint] = useState<number | null>(null);
  const [hoveredVideo, setHoveredVideo] = useState<number | null>(null);
  const [hoveredLive, setHoveredLive] = useState<number | null>(null);
  const [showVideoMarkers, setShowVideoMarkers] = useState(true);
  const [showLiveMarkers, setShowLiveMarkers] = useState(true);
  const [isHoveringCompLine, setIsHoveringCompLine] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [pinnedVideo, setPinnedVideo] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveHoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chartAreaRef = useRef<HTMLDivElement>(null);

  // Zoom & pan state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, panOffset: 0 });
  const pinchRef = useRef({ startDist: 0, startZoom: 1, startPanOffset: 0, startMidX: 0 });
  const zoomRef = useRef({ level: 1, pan: 0 });
  // Track SVG container pixel size for non-distorted markers
  const [chartSize, setChartSize] = useState({ w: 800, h: 200 });

  const handleVideoHover = useCallback((idx: number | null) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredVideo(idx);
  }, []);

  const handleVideoLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredVideo(null);
    }, 150);
  }, []);

  const handleLiveLeave = useCallback(() => {
    if (liveHoverTimeoutRef.current) {
      clearTimeout(liveHoverTimeoutRef.current);
      liveHoverTimeoutRef.current = null;
    }
    liveHoverTimeoutRef.current = setTimeout(() => {
      setHoveredLive(null);
    }, 150);
  }, []);

  const handleLiveHover = useCallback((idx: number | null) => {
    if (liveHoverTimeoutRef.current) {
      clearTimeout(liveHoverTimeoutRef.current);
      liveHoverTimeoutRef.current = null;
    }
    setHoveredLive(idx);
  }, []);

  const periodLabel = period === '24h' ? 'Last 24 hours' :
    period === '7d' ? 'Last 7 days' :
    period === '1m' ? 'Last 30 days' :
    period === '3m' ? 'Last 90 days' :
    'Last 6 months';

  // Use real data or fallback to empty array
  const activeData = data || [];
  const compData = comparisonData || [];
  
  // Use .value from ChartPoint
  const dataPoints = activeData.map(d => d.value);
  const compPoints = compData.map(d => d.value);
  
  // If no data, show empty state or filler
  const allPoints = [...dataPoints, ...compPoints];
  const hasData = dataPoints.length > 1;
  const maxVal = allPoints.length > 0 ? Math.max(...allPoints) : 100;
  const minVal = allPoints.length > 0 ? Math.min(...allPoints) : 0;
  
  // Padding for labels
  const topPadding = 20;
  const bottomPadding = 20;
  const height = 200;
  const width = 1000; 

  const normalizeY = (val: number) => {
    if (maxVal === minVal) return height / 2;
    return (height - bottomPadding) - ((val - minVal) / (maxVal - minVal)) * (height - topPadding - bottomPadding);
  };

  // Calculate video markers
  // ChartPoint uses .timestamp
  const timestamps = activeData.map(d => new Date(d.timestamp).getTime()).filter(ts => !isNaN(ts));
  const rangeStart = timestamps[0] ?? 0;
  const rangeEnd = timestamps[timestamps.length - 1] ?? rangeStart + 1;

  // Time-proportional x helpers for SVG coordinate space (0–width)
  const tsRangeDelta = Math.max(1, rangeEnd - rangeStart);
  const tsToSvgX = (ts: number) => ((ts - rangeStart) / tsRangeDelta) * width;

  // Comparison line has its own time range (previous period)
  const compTimestamps = compData.length > 1
    ? compData.map(d => new Date(d.timestamp).getTime()).filter(ts => !isNaN(ts))
    : [];
  const compRangeStart = compTimestamps[0] ?? rangeStart;
  const compRangeEnd = compTimestamps[compTimestamps.length - 1] ?? compRangeStart + 1;
  const compRangeDelta = Math.max(1, compRangeEnd - compRangeStart);
  const compTsToSvgX = (ts: number) => ((ts - compRangeStart) / compRangeDelta) * width;

  // Binary-search linear interpolation: returns the SVG Y that sits exactly on
  // the polyline at the given timestamp — used to place markers precisely on the line.
  const getInterpolatedY = (ts: number): number => {
    if (activeData.length === 0) return height / 2;
    // Binary search: find first index where timestamp >= ts
    let lo = 0, hi = activeData.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (new Date(activeData[mid].timestamp).getTime() < ts) lo = mid + 1;
      else hi = mid;
    }
    if (lo === 0) return normalizeY(dataPoints[0]);
    if (lo >= activeData.length) return normalizeY(dataPoints[activeData.length - 1]);
    const t0 = new Date(activeData[lo - 1].timestamp).getTime();
    const t1 = new Date(activeData[lo].timestamp).getTime();
    const frac = t1 === t0 ? 0 : (ts - t0) / (t1 - t0);
    return normalizeY(dataPoints[lo - 1] + frac * (dataPoints[lo] - dataPoints[lo - 1]));
  };

  const points = hasData
    ? dataPoints.map((val, index) => {
        const ts = new Date(activeData[index].timestamp).getTime();
        const x = isNaN(ts) ? (index / Math.max(1, dataPoints.length - 1)) * width : tsToSvgX(ts);
        const y = normalizeY(val);
        return `${x},${y}`;
      }).join(' ')
    : `0,${height/2} ${width},${height/2}`;

  const compLinePoints = compPoints.length > 1
    ? compData.map((d, index) => {
        const ts = new Date(d.timestamp).getTime();
        const x = isNaN(ts) ? (index / Math.max(1, compPoints.length - 1)) * width : compTsToSvgX(ts);
        const y = normalizeY(d.value);
        return `${x},${y}`;
      }).join(' ')
    : '';

  const videoMarkersRaw = (videoPosts || []).map(post => {
    if (!hasData || !post.posted_at) return null;

    let postTs: number;
    const rawAt = post.posted_at; // string in VideoPostMarker (ISO)
    postTs = new Date(rawAt).getTime();

    if (isNaN(postTs)) return null;

    const relative = (postTs - rangeStart) / tsRangeDelta;

    // Only show if inside range
    if (relative < 0 || relative > 1) return null;

    return {
      x: tsToSvgX(postTs),
      y: getInterpolatedY(postTs),
      video: post,
    };
  }).filter((m): m is { x: number; y: number; video: VideoPostMarker; index: number } => m !== null);

  // Identify top performer of the visible period
  const topViewCount = Math.max(0, ...videoMarkersRaw.map(m => m.video.view_count || 0));
  const videoMarkers = videoMarkersRaw.map(m => ({
    ...m,
    isTop: topViewCount > 0 && m.video.view_count === topViewCount
  }));

  // Calculate live event markers
  const liveMarkers = (liveEvents || []).map(event => {
    if (!hasData || !event.started_at) return null;

    const eventTs = new Date(event.started_at).getTime();
    if (isNaN(eventTs)) return null;

    const relative = (eventTs - rangeStart) / tsRangeDelta;

    if (relative < 0 || relative > 1) return null;

    return {
      x: tsToSvgX(eventTs),
      y: getInterpolatedY(eventTs),
      event,
    };
  }).filter((m): m is { x: number; y: number; event: LiveEventMarker; index: number } => m !== null);

  const growth = hasData ? dataPoints[dataPoints.length - 1] - dataPoints[0] : 0;

  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + ' M';
    if (num >= 10000) return (num / 1000).toFixed(2) + ' k';
    if (num >= 1000) return (num / 1000).toFixed(2) + ' k';
    return num.toString();
  };

  // Zoom helpers
  const svgXToPercent = (svgX: number) => {
    const viewBoxX = panOffset * width;
    const viewBoxW = width / zoomLevel;
    return ((svgX - viewBoxX) / viewBoxW) * 100;
  };

  // Time-proportional percent helpers for HTML overlays (account for zoom/pan)
  const tsToPercent = (ts: number) => svgXToPercent(tsToSvgX(ts));
  const compTsToPercent = (ts: number) => svgXToPercent(compTsToSvgX(ts));

  // Scale factors to compensate for SVG non-uniform scaling caused by preserveAspectRatio="none".
  // Applying scale(msx, msy) inside a <g transform="translate(x,y)"> counteracts viewBox distortion,
  // so circles stay circular and triangles stay proportionate at any zoom level.
  const msx = chartSize.w > 0 ? (width / zoomLevel) / chartSize.w : 1;
  const msy = chartSize.h > 0 ? height / chartSize.h : 1;

  // Keep zoom ref in sync
  zoomRef.current = { level: zoomLevel, pan: panOffset };

  // Reset zoom when period changes
  useEffect(() => {
    setZoomLevel(1);
    setPanOffset(0);
  }, [period]);

  // Generate intelligent X-axis labels based on period
  // Returns { x, text } where x is time-proportional SVG coordinate
  const getXAxisLabels = (): { x: number; text: string }[] => {
    if (!hasData) return [];
    
    const labels: { x: number; text: string; _index: number }[] = [];
    const count = activeData.length;

    const pushLabel = (i: number, text: string) => {
      const ts = new Date(activeData[i].timestamp || '').getTime();
      labels.push({ x: isNaN(ts) ? (i / Math.max(1, count - 1)) * width : tsToSvgX(ts), text, _index: i });
    };

    // Always include start and end
    if (period === '24h') {
        // Hourly (format as 14:00)
        const step = Math.max(1, Math.floor(count / 6));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            pushLabel(i, isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));
        }
    } else if (period === '7d') {
        // Daily (format as Mon 20)
        const step = Math.max(1, Math.floor(count / 7));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            pushLabel(i, isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { weekday: 'short', day: 'numeric' }));
        }
    } else if (period === '1m') {
        // Daily (format as Jan 20)
        const step = Math.max(1, Math.floor(count / 7));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            pushLabel(i, isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric' }));
        }
    } else {
        // Weekly (3m, 6m)
        const step = Math.max(1, Math.floor(count / 8));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            pushLabel(i, isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric' }));
        }
    }
    
    // Ensure last label is included if not already
    if (labels[labels.length - 1]?._index !== count - 1) {
        const lastD = new Date(activeData[count - 1].timestamp || '');
        const lastText = isNaN(lastD.getTime()) ? '' : (
            period === '24h' 
            ? lastD.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            : lastD.toLocaleDateString([], { month: 'short', day: 'numeric' })
        );
        pushLabel(count - 1, lastText);
    }
    
    return labels;
  };

  const xAxisLabels = getXAxisLabels();

  // Shared interaction handler for both mouse and touch
  const handleChartInteraction = useCallback((clientX: number, clientY: number, rect: DOMRect) => {
    if (isHoveringCompLine || !hasData) return;
    
    const x = clientX - rect.left;
    const relativeX = x / rect.width;
    // Map visible position to data position accounting for zoom/pan
    const dataRelativeX = panOffset + relativeX / zoomLevel;
    // Map mouse Y to SVG coordinate space
    const svgY = ((clientY - rect.top) / rect.height) * height;
    
    // Find closest data point by timestamp (time-proportional x)
    const hoverTs = rangeStart + dataRelativeX * tsRangeDelta;
    let closestIndex = 0;
    let minDist = Infinity;
    for (let i = 0; i < activeData.length; i++) {
      const ts = new Date(activeData[i].timestamp || '').getTime();
      const dist = isNaN(ts) ? Infinity : Math.abs(ts - hoverTs);
      if (dist < minDist) { minDist = dist; closestIndex = i; }
    }
    setHoveredPoint(closestIndex);

    // Check proximity to video markers
    // Y zone is asymmetric: no upper limit (popup appears above marker so mouse moves up toward it),
    // but only 4% below the marker.
    if (showVideoMarkers && videoMarkers.length > 0) {
      const X_THRESHOLD = 0.04;  // 4% of data width
      const Y_BELOW_MAX = 0.04; // 4% below marker
      let closestVideoIdx: number | null = null;
      let closestDist = Infinity;
      
      for (let i = 0; i < videoMarkers.length; i++) {
        const markerRelX = videoMarkers[i].x / width;
        const distX = Math.abs(dataRelativeX - markerRelX);
        const distBelow = (svgY - videoMarkers[i].y) / height; // positive = below marker
        // Accept if: within X band, not too far below marker, and mouse is above or at marker
        if (distX < X_THRESHOLD && distBelow < Y_BELOW_MAX && distX < closestDist) {
          closestDist = distX;
          closestVideoIdx = i;
        }
      }
      if (closestVideoIdx !== null) {
        handleVideoHover(closestVideoIdx);
      } else {
        handleVideoLeave();
      }
    }

    // Check proximity to live event markers (same asymmetric logic)
    if (showLiveMarkers && liveMarkers.length > 0) {
      const X_THRESHOLD = 0.04;  // 4% of data width
      const Y_BELOW_MAX = 0.04; // 4% below marker
      let closestLiveIdx: number | null = null;
      let closestDist = Infinity;

      for (let i = 0; i < liveMarkers.length; i++) {
        const markerRelX = liveMarkers[i].x / width;
        const distX = Math.abs(dataRelativeX - markerRelX);
        const distBelow = (svgY - liveMarkers[i].y) / height;
        if (distX < X_THRESHOLD && distBelow < Y_BELOW_MAX && distX < closestDist) {
          closestDist = distX;
          closestLiveIdx = i;
        }
      }
      if (closestLiveIdx !== null) {
        handleLiveHover(closestLiveIdx);
      } else {
        handleLiveLeave();
      }
    }
  }, [isHoveringCompLine, hasData, activeData, rangeStart, tsRangeDelta, height, showVideoMarkers, videoMarkers, showLiveMarkers, liveMarkers, width, handleVideoHover, handleVideoLeave, handleLiveHover, handleLiveLeave, zoomLevel, panOffset]);

  const handleChartMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isPanning) {
      const dx = e.clientX - panStartRef.current.x;
      if (Math.abs(dx) > 3) {
        const rect = e.currentTarget.getBoundingClientRect();
        const dataDx = (dx / rect.width) / zoomLevel;
        const newPan = panStartRef.current.panOffset - dataDx;
        const maxPan = Math.max(0, 1 - 1 / zoomLevel);
        setPanOffset(Math.max(0, Math.min(maxPan, newPan)));
      }
      return;
    }
    handleChartInteraction(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
  };

  const handleChartMouseLeave = () => {
    setIsPanning(false);
    if (!isHoveringCompLine) {
      setHoveredPoint(null);
    }
    handleVideoLeave();
    handleLiveLeave();
  };

  // Touch handlers for mobile
  const touchPanRef = useRef({ startX: 0, startPanOffset: 0, hasMoved: false });

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!hasData) return;
    e.preventDefault();
    // Two-finger: start pinch zoom
    if (e.touches.length === 2) {
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const rect = e.currentTarget.getBoundingClientRect();
      const midX = ((e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left) / rect.width;
      pinchRef.current = { startDist: dist, startZoom: zoomLevel, startPanOffset: panOffset, startMidX: midX };
      setIsTouching(false);
      return;
    }
    if (e.touches.length !== 1) return;
    // Clear any previously pinned video tooltip
    setPinnedVideo(null);
    setIsTouching(true);
    const touch = e.touches[0];
    touchPanRef.current = { startX: touch.clientX, startPanOffset: panOffset, hasMoved: false };
    const rect = e.currentTarget.getBoundingClientRect();
    handleChartInteraction(touch.clientX, touch.clientY, rect);
  }, [hasData, handleChartInteraction, zoomLevel, panOffset]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    // Two-finger: pinch zoom
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const { startDist, startZoom, startPanOffset, startMidX } = pinchRef.current;
      if (startDist === 0) return;
      const scale = dist / startDist;
      const newZoom = Math.max(1, Math.min(10, startZoom * scale));
      const cursorInData = startPanOffset + startMidX / startZoom;
      const newPan = cursorInData - startMidX / newZoom;
      const maxPan = Math.max(0, 1 - 1 / newZoom);
      setZoomLevel(newZoom);
      setPanOffset(Math.max(0, Math.min(maxPan, newPan)));
      return;
    }

    if (!isTouching || e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = e.currentTarget.getBoundingClientRect();

    // When zoomed in, single-finger drag pans horizontally
    if (zoomRef.current.level > 1) {
      const dxPx = touch.clientX - touchPanRef.current.startX;
      if (Math.abs(dxPx) > 5) {
        touchPanRef.current.hasMoved = true;
        const dataDx = (dxPx / rect.width) / zoomRef.current.level;
        const newPan = touchPanRef.current.startPanOffset - dataDx;
        const maxPan = Math.max(0, 1 - 1 / zoomRef.current.level);
        setPanOffset(Math.max(0, Math.min(maxPan, newPan)));
      }
    }

    handleChartInteraction(touch.clientX, touch.clientY, rect);
  }, [isTouching, handleChartInteraction]);

  const handleTouchEnd = useCallback(() => {
    setIsTouching(false);
    setHoveredPoint(null);
    // If a video tooltip is showing, pin it so user can tap image/title
    if (hoveredVideo !== null) {
      setPinnedVideo(hoveredVideo);
    }
    handleVideoHover(null);
  }, [handleVideoHover, hoveredVideo]);

  // --- Zoom: wheel handler (desktop scroll-to-zoom) ---
  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el || !hasData) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { level, pan } = zoomRef.current;
      const rect = el.getBoundingClientRect();
      const cursorRel = (e.clientX - rect.left) / rect.width;
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const newZoom = Math.max(1, Math.min(10, level * factor));
      const cursorInData = pan + cursorRel / level;
      const newPan = cursorInData - cursorRel / newZoom;
      const maxPan = Math.max(0, 1 - 1 / newZoom);
      setZoomLevel(newZoom);
      setPanOffset(Math.max(0, Math.min(maxPan, newPan)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [hasData]);

  // Track chart container size so we can draw non-distorted markers in SVG space
  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setChartSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --- Zoom: mouse-drag pan (desktop) ---
  const handleChartMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (zoomLevel <= 1) return;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, panOffset };
  }, [zoomLevel, panOffset]);

  const handleChartMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset(0);
  }, []);

  // Legend JSX
  const legend = (
    <>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-0.5 bg-[#38bdf8]"></div>
        <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Current</span>
      </div>
      {compLinePoints && (
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 border-t border-dashed border-slate-500"></div>
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Previous</span>
        </div>
      )}
      {videoMarkers.length > 0 && (
        <div 
          className={`flex items-center gap-1.5 cursor-pointer transition-opacity ${showVideoMarkers ? 'opacity-100' : 'opacity-50'}`}
          onClick={() => setShowVideoMarkers(!showVideoMarkers)}
          title="Toggle video markers"
        >
          <div className="w-2 h-2 border-2 border-[#ff00ff]"></div>
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Video Post</span>
        </div>
      )}
      {liveMarkers.length > 0 && (
        <div 
          className={`flex items-center gap-1.5 cursor-pointer transition-opacity ${showLiveMarkers ? 'opacity-100' : 'opacity-50'}`}
          onClick={() => setShowLiveMarkers(!showLiveMarkers)}
          title="Toggle live event markers"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" className="flex-shrink-0">
            <polygon points="5,1 9,9 1,9" fill="none" stroke="#22C55E" strokeWidth="1.5" />
          </svg>
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Live Event</span>
        </div>
      )}
    </>
  );

  // Actions JSX (growth stats)
  const actions = (
    <div className="text-right">
      <span className={`text-2xl font-bold ${growth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
        {growth >= 0 ? '+' : ''}{growth.toLocaleString()}
      </span>
      <p className="text-xs text-slate-400 uppercase tracking-wide">followers</p>
    </div>
  );

  return (
    <>
    <ChartCard
      title="Follower Growth"
      subtitle={periodLabel}
      legend={legend}
      actions={actions}
      isLoading={isLoading}
      className="mb-8"
    >
      <div className="relative h-48 sm:h-64 w-full pt-2 pb-10">
        {hasData ? (
          <div className="relative h-full w-full">
            {/* Y-Axis Labels */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] text-slate-500 pointer-events-none z-10 -ml-1 pr-2 border-r border-slate-800/30">
                <span>{formatLargeNumber(maxVal)}</span>
                <span>{formatLargeNumber(minVal + (maxVal - minVal) * 0.75)}</span>
                <span>{formatLargeNumber(minVal + (maxVal - minVal) * 0.5)}</span>
                <span>{formatLargeNumber(minVal + (maxVal - minVal) * 0.25)}</span>
                <span>{formatLargeNumber(minVal)}</span>
            </div>

            <div className="ml-8 h-full relative">
                {/* Chart content (clipped) */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Zoom indicator */}
                    {zoomLevel > 1.01 && (
                      <div className="absolute top-1 right-1 z-50 flex items-center gap-1.5">
                        <span className="text-[10px] text-slate-400 bg-slate-800/90 backdrop-blur-sm px-1.5 py-0.5 rounded">
                          {zoomLevel.toFixed(1)}x
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); resetZoom(); }}
                          className="text-[10px] text-slate-300 bg-slate-800/90 backdrop-blur-sm hover:bg-slate-700 px-1.5 py-0.5 rounded cursor-pointer transition-colors border border-slate-700"
                        >
                          Reset
                        </button>
                      </div>
                    )}

                    {/* Hover/touch detection area for entire chart */}
                    <div 
                        ref={chartAreaRef}
                        className={`absolute inset-0 z-10 ${zoomLevel > 1 ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-crosshair'}`}
                        style={{ touchAction: 'none' }}
                        onMouseDown={handleChartMouseDown}
                        onMouseMove={handleChartMouseMove}
                        onMouseUp={handleChartMouseUp}
                        onMouseLeave={handleChartMouseLeave}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchEnd}
                    />
                    
                    <svg viewBox={`${panOffset * width} 0 ${width / zoomLevel} ${height}`} className="w-full h-full" preserveAspectRatio="none">
                        {/* Vertical Grid Lines */}
                        {xAxisLabels.map((lbl, i) => (
                            <line 
                                key={`v-${i}`}
                                x1={lbl.x} 
                                y1={topPadding} 
                                x2={lbl.x} 
                                y2={height - bottomPadding} 
                                stroke="#334155" 
                                strokeWidth="0.5" 
                                opacity="0.2"
                                vectorEffect="non-scaling-stroke"
                            />
                        ))}

                        {/* Horizontal Grid lines */}
                        <line x1="0" y1={height - bottomPadding} x2={width} y2={height - bottomPadding} stroke="#334155" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                        <line x1="0" y1={topPadding + (height - bottomPadding - topPadding) * 0.75} x2={width} y2={topPadding + (height - bottomPadding - topPadding) * 0.75} stroke="#334155" strokeWidth="0.5" opacity="0.2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke"/>
                        <line x1="0" y1={(height - bottomPadding + topPadding) / 2} x2={width} y2={(height - bottomPadding + topPadding) / 2} stroke="#334155" strokeWidth="0.5" opacity="0.3" vectorEffect="non-scaling-stroke" />
                        <line x1="0" y1={topPadding + (height - bottomPadding - topPadding) * 0.25} x2={width} y2={topPadding + (height - bottomPadding - topPadding) * 0.25} stroke="#334155" strokeWidth="0.5" opacity="0.2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke"/>
                        <line x1="0" y1={topPadding} x2={width} y2={topPadding} stroke="#334155" strokeWidth="0.5" strokeDasharray="4 4" vectorEffect="non-scaling-stroke"/>
                        
                        {/* Comparison Line (Previous Period) */}
                        {compLinePoints && (
                          <polyline
                            fill="none"
                            stroke="#64748b"
                            strokeWidth="1.5"
                            strokeDasharray="4 4"
                            points={compLinePoints}
                            opacity="0.5"
                            vectorEffect="non-scaling-stroke"
                          />
                        )}


                        {/* Area fill */}
                        <path
                        d={`M0,${height - bottomPadding} ${points} L${width},${height - bottomPadding} Z`}
                        fill="url(#gradient)"
                        opacity="0.15"
                        className="pointer-events-none"
                        />

                        
                        {/* Line */}
                        <polyline
                        fill="none"
                        stroke="#38bdf8"
                        strokeWidth="3"
                        points={points}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                        />

                        {/* Video Post Markers - pixel-accurate via inverse scale transform */}
                        {showVideoMarkers && videoMarkers.map((m, idx) => (
                          <g
                            key={`video-svg-${idx}`}
                            transform={`translate(${m.x},${m.y}) scale(${msx},${msy})`}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => handleVideoHover(idx)}
                            onMouseLeave={handleVideoLeave}
                            onClick={() => {
                              if (username && m.video.id) {
                                navigate(`/dashboard/${username}/video/${m.video.id}`);
                              }
                            }}
                          >
                            <rect
                              x={-7} y={-7} width={14} height={14}
                              fill={m.isTop ? '#ff00ff' : 'transparent'}
                              stroke="#ff00ff"
                              strokeWidth="3"
                              vectorEffect="non-scaling-stroke"
                            />
                          </g>
                        ))}

                        {/* Live Event Markers - pixel-accurate via inverse scale transform */}
                        {showLiveMarkers && liveMarkers.map((m, idx) => (
                          <g
                            key={`live-svg-${idx}`}
                            transform={`translate(${m.x},${m.y}) scale(${msx},${msy})`}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredLive(idx)}
                            onMouseLeave={() => setHoveredLive(null)}
                          >
                            {/* Equilateral triangle: circumradius 9px, vertices at -90°, 30°, 150° */}
                            <polygon
                              points="0,-9 7.794,4.5 -7.794,4.5"
                              fill="none"
                              stroke="#22C55E"
                              strokeWidth="3"
                              vectorEffect="non-scaling-stroke"
                            />
                          </g>
                        ))}

                        <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#38bdf8" />
                            <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                        </linearGradient>
                        </defs>

                    </svg>

                    {/* Interactive circles for comparison line - HTML overlay */}
                    <div className="absolute inset-0 pointer-events-none z-20">
                        {compPoints.map((val, index) => (
                            <div
                                key={`comp-interact-${index}`}
                                className="absolute pointer-events-auto cursor-pointer"
                                style={{
                                    left: `${compTsToPercent(new Date(compData[index]?.timestamp || '').getTime())}%`,
                                    top: `${(normalizeY(val) / height) * 100}%`,
                                    width: '24px',
                                    height: '24px',
                                    transform: 'translate(-50%, -50%)'
                                }}
                                onMouseEnter={() => {
                                    setIsHoveringCompLine(true);
                                    setHoveredCompPoint(index);
                                    setHoveredPoint(null);
                                }}
                                onMouseLeave={() => {
                                    setIsHoveringCompLine(false);
                                    setHoveredCompPoint(null);
                                }}
                            />
                        ))}
                    </div>

                    {/* Active Point Indicators - HTML Overlay (to preserve circular shape) */}
                    <div className="absolute inset-0 pointer-events-none">
                        {/* Comparison Line Dot */}
                        {hoveredCompPoint !== null && compPoints[hoveredCompPoint] !== undefined && (
                            <div
                                className="absolute rounded-full border-2 border-white bg-slate-500 shadow-sm"
                                style={{
                                    left: `${compTsToPercent(new Date(compData[hoveredCompPoint]?.timestamp || '').getTime())}%`,
                                    top: `${(normalizeY(compPoints[hoveredCompPoint]) / height) * 100}%`,
                                    width: '10px',
                                    height: '10px',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        )}

                        {/* Current Line Dot */}
                        {hoveredPoint !== null && dataPoints[hoveredPoint] !== undefined && (
                            <div
                                className="absolute rounded-full border-2 border-white bg-[#38bdf8] shadow-sm"
                                style={{
                                    left: `${tsToPercent(new Date(activeData[hoveredPoint]?.timestamp || '').getTime())}%`,
                                    top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
                                    width: '12px',
                                    height: '12px',
                                    transform: 'translate(-50%, -50%)'
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Tooltips and Labels Layer (NOT clipped) */}
                <div className="absolute inset-0 pointer-events-none">
                    {/* Video Hover/Pinned Tooltip */}
                    {(() => {
                        const activeVideoIdx = hoveredVideo ?? pinnedVideo;
                        if (activeVideoIdx === null || !videoMarkers[activeVideoIdx]) return null;
                        const vm = videoMarkers[activeVideoIdx]!;
                        return (
                        <div 
                            className="absolute z-50 bg-slate-900/95 backdrop-blur-md border border-slate-700 p-3 rounded-xl shadow-2xl pointer-events-auto max-w-[320px] w-max cursor-pointer hover:border-slate-600 transition-colors"
                            style={{ 
                          left: `${svgXToPercent(vm.x)}%`, 
                                top: `${(vm.y / height) * 100}%`,
                                transform: 'translate(-50%, -115%)' 
                            }}
                            onMouseEnter={() => handleVideoHover(activeVideoIdx)}
                            onMouseLeave={handleVideoLeave}
                            onClick={() => {
                              setPinnedVideo(null);
                              if (username && vm.video.id) {
                                navigate(`/dashboard/${username}/video/${vm.video.id}`);
                              }
                            }}
                        >
                            <div className="flex gap-3 mb-2">
                                {/* Video Thumbnail - clickable to play embed */}
                                <PlayableVideoThumbnail
                                    coverImageUrl={vm.video.cover_image_url}
                                    title={vm.video.title || 'Video thumbnail'}
                                    tiktokId={vm.video.tiktok_id}
                                    username={username}
                                    videoId={vm.video.id}
                                    className="w-16 h-20"
                                    stopPropagation
                                />
                                
                                {/* Title and Date column */}
                                <div className="flex flex-col justify-between flex-1 min-w-0">
                                    <p 
                                        className="text-white text-xs font-bold line-clamp-2 leading-snug"
                                    >
                                        {vm.video.title || 'Untitled Video'}
                                        {vm.isTop && <span className="ml-2 text-[10px] text-[#ff00ff] bg-[#ff00ff]/10 px-1.5 py-0.5 rounded border border-[#ff00ff]/20">TOP</span>}
                                    </p>
                                    <p className="text-slate-400 text-[10px] font-medium">
                                        {new Date(vm.video.posted_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}, {new Date(vm.video.posted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                                <div className="flex items-center text-[10px] gap-1">
                                    <span className="font-bold" style={{ color: COLOR_VIEWS }}>{vm.video.view_count.toLocaleString()}</span>
                                    <Eye className="w-3 h-3" style={{ color: COLOR_VIEWS }} />
                                </div>
                                <div className="flex items-center text-[10px] gap-1">
                                    <span className="font-bold" style={{ color: COLOR_LIKES }}>{vm.video.like_count.toLocaleString()}</span>
                                    <Heart className="w-3 h-3" style={{ color: COLOR_LIKES }} />
                                </div>
                                <div className="flex items-center text-[10px] gap-1">
                                    <span className="font-bold" style={{ color: COLOR_COMMENTS }}>{(vm.video.comment_count || 0).toLocaleString()}</span>
                                    <MessageCircle className="w-3 h-3" style={{ color: COLOR_COMMENTS }} />
                                </div>
                                <div className="flex items-center text-[10px] gap-1">
                                    <span className="font-bold" style={{ color: COLOR_SHARES }}>{(vm.video.share_count || 0).toLocaleString()}</span>
                                    <Share2 className="w-3 h-3" style={{ color: COLOR_SHARES }} />
                                </div>
                            </div>
                        </div>
                        );
                    })()}

                    {/* Live Event Hover Tooltip */}
                    {hoveredLive !== null && liveMarkers[hoveredLive] && (
                        <div 
                            className="absolute z-50 bg-slate-900/95 backdrop-blur-md border border-emerald-700/50 p-3 rounded-xl shadow-2xl pointer-events-auto max-w-[260px] w-max"
                            style={{ 
                                left: `${svgXToPercent(liveMarkers[hoveredLive].x)}%`, 
                                top: `${(liveMarkers[hoveredLive].y / height) * 100}%`,
                                transform: 'translate(-50%, -115%)' 
                            }}
                            onMouseEnter={() => handleLiveHover(hoveredLive)}
                            onMouseLeave={handleLiveLeave}
                        >
                            <div className="flex items-center gap-2 mb-1.5">
                                <svg width="12" height="12" viewBox="0 0 16 16" className="flex-shrink-0">
                                    <polygon points="8,1 15,15 1,15" fill="none" stroke="#22C55E" strokeWidth="2" />
                                </svg>
                                <span className="text-xs font-bold text-white truncate">
                                    {liveMarkers[hoveredLive].event.title || 'Live Stream'}
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-400 mb-1">
                                {new Date(liveMarkers[hoveredLive].event.started_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}, {new Date(liveMarkers[hoveredLive].event.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </p>
                            {liveMarkers[hoveredLive].event.duration_minutes != null && (
                                <p className="text-[10px] text-emerald-400 font-medium">
                                    Duration: {liveMarkers[hoveredLive].event.duration_minutes! >= 60
                                        ? `${Math.floor(liveMarkers[hoveredLive].event.duration_minutes! / 60)}h ${liveMarkers[hoveredLive].event.duration_minutes! % 60}m`
                                        : `${liveMarkers[hoveredLive].event.duration_minutes}m`}
                                </p>
                            )}
                            {liveMarkers[hoveredLive].event.notes && (
                                <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">{liveMarkers[hoveredLive].event.notes}</p>
                            )}
                        </div>
                    )}

                    {/* X-Axis Labels — overflow-hidden clips labels that scroll outside chart bounds */}
                    <div className="absolute left-0 right-0 bottom-[-22px] h-5 overflow-hidden text-[10px] text-slate-500">
                        {xAxisLabels.map((lbl, idx) => (
                            <div 
                                key={idx} 
                                className="absolute -translate-x-1/2 whitespace-nowrap"
                                style={{ left: `${svgXToPercent(lbl.x)}%` }}
                            >
                                {lbl.text}
                            </div>
                        ))}
                    </div>

                    {/* Current Line Tooltip */}
                    {hoveredPoint !== null && (
                        <div 
                            className="absolute bg-slate-800 text-white text-xs px-2 py-1.5 rounded border border-slate-700 shadow-2xl pointer-events-none z-40 transition-transform duration-75"
                            style={{
                                left: `${tsToPercent(new Date(activeData[hoveredPoint]?.timestamp || '').getTime())}%`,
                                top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
                                transform: getTooltipTransform(hoveredPoint, dataPoints.length)
                            }}
                        >
                            <div className="font-bold whitespace-nowrap">{activeData[hoveredPoint].value.toLocaleString()}</div>
                            <div className="text-[10px] text-slate-400 whitespace-nowrap">
                                {new Date(activeData[hoveredPoint].timestamp || '').toLocaleString([], { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    hour: '2-digit', 
                                    minute: '2-digit', 
                                    hour12: false 
                                })}
                            </div>
                        </div>
                    )}

                    {/* Comparison Line Tooltip */}
                    {hoveredCompPoint !== null && compData[hoveredCompPoint] && (
                        <div 
                            className="absolute bg-slate-700/95 text-white text-xs px-2 py-1.5 rounded border border-slate-600 shadow-2xl pointer-events-none z-40 transition-transform duration-75"
                            style={{
                                left: `${compTsToPercent(new Date(compData[hoveredCompPoint]?.timestamp || '').getTime())}%`,
                                top: `${(normalizeY(compPoints[hoveredCompPoint]) / height) * 100}%`,
                                transform: getTooltipTransform(hoveredCompPoint, compPoints.length)
                            }}
                        >
                            <div className="text-[9px] text-slate-400 whitespace-nowrap mb-0.5">Previous Period</div>
                            <div className="font-bold whitespace-nowrap">{compData[hoveredCompPoint].value.toLocaleString()}</div>
                            <div className="text-[10px] text-slate-300 whitespace-nowrap">
                                {new Date(compData[hoveredCompPoint].timestamp || '').toLocaleString([], { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    hour: '2-digit', 
                                    minute: '2-digit', 
                                    hour12: false 
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-500">
            No growth data available for this period
          </div>
        )}
      </div>
    </ChartCard>

    </>
  );
};

export default GrowthChart;
